import * as React from "react";
import { I18N_NS } from "Config/i18n";
import { Input, Button, Form, InputRef, Tooltip } from "antd";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react";
import { ModalComponentProps } from "Components/General/Modal/ModalContext";
import { documentsTableStore } from "../Stores/DocumentsTableStore";
import { ButtonsRow } from "Components/Form/ButtonsRow";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { dirnameCharValidator } from "utils/inputValidation";

interface FormBody extends Pick<API_FolderDataEntity, "name" | "description"> {}

export interface FolderNameFormProps extends ModalComponentProps {
  initialName?: string;
  isEdit?: boolean;
  folderId?: number;
  parentFolderId?: number;
  description?: string;
  initialDescription?: string;
}

export const FolderNameForm = observer((props: FolderNameFormProps) => {
  const inputRef: React.RefObject<InputRef> = React.useRef();
  const { t } = useTranslation(I18N_NS.PROJECT);
  const [loading, setLoading] = React.useState(false);

  const handleFinish = async (values: FormBody) => {
    try {
      setLoading(true);
      if (props.isEdit) {
        if (props.folderId) {
          await documentsTableStore.renameFolderById(props.folderId, values.name, values.description);
        } else {
          await documentsTableStore.renameCurrentFolder(values.name, values.description);
        }
      } else {
        await documentsTableStore.createFolder(values.name, props.parentFolderId, values.description);
      }
      props.hideModal();
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const folderNameValidator = React.useCallback((_: unknown, value: string) => dirnameCharValidator(_, value, t), [t]);

  React.useEffect(() => {
    inputRef.current.focus();
  }, [inputRef.current]);

  const { initialName, initialDescription } = props;
  return (
    <Form onFinish={handleFinish} layout="vertical">
      <Form.Item
        name="name"
        initialValue={initialName}
        rules={[
          { required: true, message: t("This field is required") },
          { required: true, validator: folderNameValidator },
        ]}
      >
        <Input
          placeholder={t("Foldername")}
          ref={inputRef}
          suffix={
            <Tooltip
              title={t("Unallowed characters: {{unallowedChars}}", {
                unallowedChars: t('/\\:*?"<>|@&%'),
                interpolation: { escapeValue: false },
              })}
            >
              <span>
                <FontAwesomeIcon icon={["far", "info-circle"]} color="#0fb9b1" fixedWidth={true} />
              </span>
            </Tooltip>
          }
        />
      </Form.Item>
      <Form.Item name="description" initialValue={initialDescription}>
        <Input.TextArea placeholder={t("Description")} />
      </Form.Item>
      <ButtonsRow>
        <Button loading={loading} htmlType="submit" type="primary">
          {initialName ? t("Edit") : t("Create")}
        </Button>
      </ButtonsRow>
    </Form>
  );
});
